import * as React from "react";
import { FunctionComponent, useState, useRef } from "react";
import { ConnectedProps, connect, MapStateToProps } from "react-redux";
import {
  StoreState,
  ColorScheme,
  Language,
  NavLinkWithIcon,
  ActionLinkItem,
  PageState,
  MainMenuItem,
} from "../types";
import LanguageMenu from "./LanguageMenu";
import { getActiveSite } from "../selectors/sites";
import {
  getActionAndContactLinks,
  getFallbackLanguage,
  getTopLevelMainMenu,
  getActivePagePath,
} from "../utils/utils";
import HeaderSidebarIconNav from "./HeaderSidebarIconNav";
import { style } from "typestyle";
import * as ClassNames from "classnames";
import Icon from "./Icon";
import HamburgerMenuList from "./HamburgerMenuList";
import { RemoveScroll } from "react-remove-scroll";

interface Props {
  scheme: ColorScheme;
  languageId: Language;
  isPreview: boolean;
  pageId: string;
  actionLinks: ActionLinkItem[];
}

interface StateProps {
  navLinks: NavLinkWithIcon[];
  pages: PageState;
  fallbackLanguageId: Language | undefined;
  activePagePath: string[];
  menuItems: MainMenuItem[];
}

type ReduxProps = ConnectedProps<typeof connector>;

const HeaderSidebar: FunctionComponent<Props & ReduxProps> = ({
  scheme,
  languageId,
  isPreview,
  navLinks,
  pageId,
  pages,
  fallbackLanguageId,
  menuItems,
  activePagePath,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div
      className={ClassNames(
        "HeaderSidebar",
        style({ backgroundColor: scheme.main.background })
      )}
    >
      <div className="HeaderSidebar__MenuButtonWrapper" ref={ref}>
        <button
          type="button"
          className="HeaderSidebar__MenuButton Btn--bare"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <Icon
            className={ClassNames(
              "HeaderSidebar__MenuIcon",
              style({ fill: scheme.main.text })
            )}
            glyph="view-headline"
          />
        </button>
        {isMenuOpen && (
          <div
            className="HeaderSidebar__MenuWrapper"
            onClick={(event) => {
              // Close menu when clicking on the wrapper but not in the menu
              event.currentTarget === event.target && setIsMenuOpen(false);
            }}
          >
            <RemoveScroll>
              <HamburgerMenuList
                className="HeaderSidebar__Menu"
                scheme={scheme}
                isPreview={isPreview}
                pages={pages}
                languageId={languageId}
                topLevelMainMenuItems={menuItems}
                activePagePath={activePagePath}
                fallbackLanguageId={fallbackLanguageId}
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                outerRef={ref}
                showMenuCaption={true}
              />
            </RemoveScroll>
          </div>
        )}
      </div>

      <HeaderSidebarIconNav
        isPreview={isPreview}
        navLinks={navLinks}
        scheme={scheme}
      />

      <LanguageMenu
        isPreview={isPreview}
        languageId={languageId}
        scheme={scheme}
        pageId={pageId}
        variant="horizontal"
        switchToSelectLimit={1}
      />
    </div>
  );
};

const mapStateToProps: MapStateToProps<StateProps, Props, StoreState> = (
  { pages, sites, accommodation },
  { languageId, actionLinks, isPreview, pageId }
): StateProps => {
  const site = getActiveSite(sites);
  const fallbackLanguageId = getFallbackLanguage(site, languageId);

  return {
    pages,
    navLinks: getActionAndContactLinks(accommodation[languageId], actionLinks),
    fallbackLanguageId,
    menuItems: getTopLevelMainMenu({
      pages,
      languageId,
      isPreview,
      fallbackLanguageId,
    }),
    activePagePath: getActivePagePath(pageId, pages.byParentId),
  };
};

const connector = connect(mapStateToProps);

export default connector(HeaderSidebar);
